import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useProductUnits } from "@/features/produtos/hooks/useProductUnits";
import { useLocalEstoqueCombo } from "@/features/estoque/hooks/useLocalEstoque";
import type { ProdutoLocalEstoqueRow, SalvarLocalEstoqueRequest } from "@/features/produtos/hooks/useProdutoLocaisEstoque";

const EMPTY: SalvarLocalEstoqueRequest = {
  localEstoqueId: 0, unidadeEntradaId: 0, unidadeSaidaId: 0,
  unidadeEntradaTributavelId: 0, unidadeSaidaTributavelId: 0,
  fatorConversaoEntrada: 1, fatorConversaoSaida: 1,
  fatorConversaoEntradaTributavel: 1, fatorConversaoSaidaTributavel: 1,
  vendaFracionada: false, padraoEntrada: false, padraoSaida: false,
  estoqueMinimo: 0, estoqueMaximo: 0, volume: 0, unidadeVolume: null, peso: 0, unidadePeso: null,
};

interface Props {
  open:    boolean;
  row:     ProdutoLocalEstoqueRow | null;
  onClose: () => void;
  onSave:  (data: SalvarLocalEstoqueRequest, id?: number) => void;
}

export function LocalEstoqueDialog({ open, row, onClose, onSave }: Props) {
  const { units } = useProductUnits();
  const { data: locais = [] } = useLocalEstoqueCombo();
  const [f, setF] = useState<SalvarLocalEstoqueRequest>(EMPTY);

  useEffect(() => {
    if (open) setF(row ? {
      localEstoqueId: row.localEstoqueId, unidadeEntradaId: row.unidadeEntradaId, unidadeSaidaId: row.unidadeSaidaId,
      unidadeEntradaTributavelId: row.unidadeEntradaTributavelId, unidadeSaidaTributavelId: row.unidadeSaidaTributavelId,
      fatorConversaoEntrada: row.fatorConversaoEntrada, fatorConversaoSaida: row.fatorConversaoSaida,
      fatorConversaoEntradaTributavel: row.fatorConversaoEntradaTributavel, fatorConversaoSaidaTributavel: row.fatorConversaoSaidaTributavel,
      vendaFracionada: row.vendaFracionada, padraoEntrada: row.padraoEntrada, padraoSaida: row.padraoSaida,
      estoqueMinimo: row.estoqueMinimo, estoqueMaximo: row.estoqueMaximo,
      volume: row.volume, unidadeVolume: row.unidadeVolume ?? null, peso: row.peso, unidadePeso: row.unidadePeso ?? null,
    } : EMPTY);
  }, [open, row]);

  const set = <K extends keyof SalvarLocalEstoqueRequest>(k: K, v: SalvarLocalEstoqueRequest[K]) => setF(p => ({ ...p, [k]: v }));
  const numField = (label: string, key: keyof SalvarLocalEstoqueRequest) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type="number" step="0.0001" min="0" className="h-8 text-sm"
        value={(f[key] as number) ?? ""} onChange={(e) => set(key, (parseFloat(e.target.value) || 0) as never)} />
    </div>
  );
  const unitSelect = (label: string, key: keyof SalvarLocalEstoqueRequest) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select value={f[key] ? String(f[key]) : "__none__"} onValueChange={(v) => set(key, (v === "__none__" ? 0 : Number(v)) as never)}>
        <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">—</SelectItem>
          {units.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.sigla} — {u.descricao}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{row ? "Editar" : "Nova"} configuração de estoque por local</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Local de Estoque *</Label>
            <Select value={f.localEstoqueId ? String(f.localEstoqueId) : "__none__"} onValueChange={(v) => set("localEstoqueId", v === "__none__" ? 0 : Number(v))}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Selecione o local" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {(locais as { id: number; descricao?: string }[]).map(l => <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs font-semibold text-muted-foreground uppercase">Unidades</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {unitSelect("Un. Entrada", "unidadeEntradaId")}
            {unitSelect("Un. Saída", "unidadeSaidaId")}
            {unitSelect("Un. Entrada Trib.", "unidadeEntradaTributavelId")}
            {unitSelect("Un. Saída Trib.", "unidadeSaidaTributavelId")}
          </div>

          <p className="text-xs font-semibold text-muted-foreground uppercase">Fatores de conversão</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {numField("Conv. Entrada", "fatorConversaoEntrada")}
            {numField("Conv. Saída", "fatorConversaoSaida")}
            {numField("Conv. Entrada Trib.", "fatorConversaoEntradaTributavel")}
            {numField("Conv. Saída Trib.", "fatorConversaoSaidaTributavel")}
          </div>

          <p className="text-xs font-semibold text-muted-foreground uppercase">Estoque & dimensões</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {numField("Estoque Mínimo", "estoqueMinimo")}
            {numField("Estoque Máximo", "estoqueMaximo")}
            {numField("Volume", "volume")}
            {numField("Peso", "peso")}
          </div>

          <div className="flex flex-wrap gap-6 pt-1">
            <div className="flex items-center gap-2"><Switch checked={f.vendaFracionada} onCheckedChange={(v) => set("vendaFracionada", v)} /><Label className="text-sm">Venda Fracionada</Label></div>
            <div className="flex items-center gap-2"><Switch checked={f.padraoEntrada} onCheckedChange={(v) => set("padraoEntrada", v)} /><Label className="text-sm">Padrão Entrada</Label></div>
            <div className="flex items-center gap-2"><Switch checked={f.padraoSaida} onCheckedChange={(v) => set("padraoSaida", v)} /><Label className="text-sm">Padrão Saída</Label></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onSave(f, row?.id)} disabled={!f.localEstoqueId}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
